import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../lib/auth';
import {
  LayoutDashboard, Package, ShoppingBag, Boxes,
  Users, LogOut, Bell, Layers, Tag, Image as ImageIcon, Settings as SettingsIcon,
  Star, Gift, ArrowLeftRight, BookOpen, BarChart3, Webhook, Shield, Sliders, FolderTree,
  Menu, X
} from 'lucide-react';

const navItems = [
  { to: '/dashboard',   icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/products',    icon: Package,         label: 'Products' },
  { to: '/categories',  icon: FolderTree,      label: 'Categories' },
  { to: '/collections', icon: Layers,          label: 'Collections' },
  { to: '/orders',      icon: ShoppingBag,     label: 'Orders' },
  { to: '/inventory',   icon: Boxes,           label: 'Inventory' },
  { to: '/transfers',   icon: ArrowLeftRight,  label: 'Transfers' },
  { to: '/discounts',   icon: Tag,             label: 'Discounts' },
  { to: '/gift-cards',  icon: Gift,            label: 'Gift Cards' },
  { to: '/customers',   icon: Users,           label: 'Customers' },
  { to: '/reviews',     icon: Star,            label: 'Reviews' },
  { to: '/journal',     icon: BookOpen,        label: 'Journal' },
  { to: '/reports',     icon: BarChart3,       label: 'Reports' },
  { to: '/webhooks',    icon: Webhook,         label: 'Webhooks' },
  { to: '/content',     icon: Sliders,         label: 'Visual Sections' },
  { to: '/media',       icon: ImageIcon,       label: 'Media' },
  { to: '/staff',       icon: Shield,          label: 'Staff' },
  { to: '/settings',    icon: SettingsIcon,    label: 'Settings' },
];

export default function AdminLayout() {
  const { session, signOut } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close mobile drawer when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="admin-shell">
      {/* Mobile Backdrop Overlay */}
      <div
        className={`admin-sidebar-backdrop ${mobileOpen ? 'open' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar Drawer */}
      <aside className={`admin-sidebar ${mobileOpen ? 'open' : ''}`}>
        <div style={styles.sidebarLogo}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div>
              <span style={styles.logoText}>ZARIN</span>
              <span style={styles.logoAdmin}>ADMIN</span>
            </div>
            <button
              className="admin-sidebar-close-btn"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <nav style={styles.nav}>
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
              })}
            >
              <Icon size={18} strokeWidth={1.5} />
              <span style={styles.navLabel}>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <div style={styles.userAvatar}>
              {(session?.email ?? 'A')[0]!.toUpperCase()}
            </div>
            <div>
              <p style={styles.userName}>{session?.email?.split('@')[0] ?? 'Admin'}</p>
              <p style={styles.userRole}>{session?.role ?? 'viewer'}</p>
            </div>
          </div>
          <button onClick={handleSignOut} style={styles.signOutBtn} title="Sign out">
            <LogOut size={16} strokeWidth={1.5} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="admin-main">
        {/* Top bar */}
        <header className="admin-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="admin-hamburger-btn"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu size={20} strokeWidth={1.75} />
            </button>
            <div style={styles.topbarLeft}>
              <span style={styles.topbarBreadcrumb}>Admin Panel</span>
            </div>
          </div>
          <div style={styles.topbarRight}>
            <button style={styles.iconBtn} aria-label="Notifications">
              <Bell size={18} strokeWidth={1.5} />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Inline styles — admin dark theme
const styles: Record<string, React.CSSProperties> = {
  shell: {
    display:      'flex',
    height:       '100dvh',
    overflow:     'hidden',
    background:   'var(--admin-bg)',
  },
  sidebar: {
    width:           'var(--sidebar-width)',
    flexShrink:      0,
    display:         'flex',
    flexDirection:   'column',
    background:      'var(--admin-surface)',
    borderRight:     '1px solid var(--admin-border)',
    transition:      'width var(--transition-slow)',
  },
  sidebarLogo: {
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'flex-start',
    padding:         '20px 20px 16px',
    borderBottom:    '1px solid var(--admin-border)',
    gap:             2,
  },
  logoText: {
    fontFamily:      'var(--font-serif)',
    fontSize:        22,
    fontWeight:      300,
    letterSpacing:   '0.2em',
    color:           'var(--admin-text)',
  },
  logoAdmin: {
    fontFamily:      'var(--font-sans)',
    fontSize:        9,
    fontWeight:      600,
    letterSpacing:   '0.35em',
    color:           'var(--admin-accent)',
  },
  nav: {
    flex:            1,
    display:         'flex',
    flexDirection:   'column',
    gap:             2,
    padding:         '12px 8px',
    overflowY:       'auto',
  },
  navItem: {
    display:         'flex',
    alignItems:      'center',
    gap:             10,
    padding:         '9px 12px',
    borderRadius:    'var(--radius-md)',
    color:           'var(--admin-text-muted)',
    textDecoration:  'none',
    fontSize:        13,
    fontWeight:      400,
    transition:      'background var(--transition), color var(--transition)',
    cursor:          'pointer',
  },
  navItemActive: {
    background:  'var(--admin-accent-dim)',
    color:       'var(--admin-accent)',
    fontWeight:  500,
  },
  navLabel: {
    letterSpacing: '0.01em',
  },
  sidebarFooter: {
    padding:         '12px 8px',
    borderTop:       '1px solid var(--admin-border)',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'space-between',
    gap:             8,
  },
  userInfo: {
    display:   'flex',
    alignItems:'center',
    gap:       10,
    minWidth:  0,
    flex:      1,
  },
  userAvatar: {
    width:           32,
    height:          32,
    borderRadius:    '50%',
    background:      'var(--admin-accent)',
    color:           '#1A1A1A',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    fontSize:        13,
    fontWeight:      700,
    flexShrink:      0,
  },
  userName: {
    fontSize:        13,
    fontWeight:      500,
    color:           'var(--admin-text)',
    whiteSpace:      'nowrap',
    overflow:        'hidden',
    textOverflow:    'ellipsis',
    maxWidth:        120,
  },
  userRole: {
    fontSize:   11,
    color:      'var(--admin-text-subtle)',
    textTransform: 'capitalize',
  },
  signOutBtn: {
    display:      'flex',
    alignItems:   'center',
    justifyContent:'center',
    width:        32,
    height:       32,
    borderRadius: 'var(--radius-md)',
    background:   'transparent',
    color:        'var(--admin-text-muted)',
    border:       'none',
    cursor:       'pointer',
    flexShrink:   0,
    transition:   'background var(--transition), color var(--transition)',
  },
  main: {
    flex:         1,
    display:      'flex',
    flexDirection:'column',
    overflow:     'hidden',
    minWidth:     0,
  },
  topbar: {
    height:           'var(--topbar-height)',
    display:          'flex',
    alignItems:       'center',
    justifyContent:   'space-between',
    padding:          '0 24px',
    borderBottom:     '1px solid var(--admin-border)',
    background:       'var(--admin-surface)',
    flexShrink:       0,
  },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  topbarBreadcrumb: { fontSize: 13, color: 'var(--admin-text-muted)' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 4 },
  iconBtn: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    width:           36,
    height:          36,
    borderRadius:    'var(--radius-md)',
    background:      'transparent',
    color:           'var(--admin-text-muted)',
    border:          'none',
    cursor:          'pointer',
    transition:      'background var(--transition)',
  },
  content: {
    flex:       1,
    overflowY:  'auto',
    padding:    24,
  },
};
